// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';

import React, { useCallback } from 'react';
import { Row, Col } from 'antd/lib/grid';
import Form from 'antd/lib/form';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import InputNumber from 'antd/lib/input-number';
import CVATTooltip from 'components/common/cvat-tooltip';

import { CombinedState } from 'reducers';
import { useDispatch, useSelector } from 'react-redux';
import { ReferenceJobType } from 'cvat-core/src/enums';
import { Reference } from 'cvat-core-wrapper';
import { createReferenceJobAsync } from 'actions/reference-jobs-actions';
import { useHistory } from 'react-router';
import Space from 'antd/lib/space';
import { QuestionCircleOutlined } from '@ant-design/icons';

export enum FrameSelectionMethod {
    RANDOM = 'random_uniform',
}

interface ReferenceJobDataMutual {
    reference_id: number;
    frame_selection_method: FrameSelectionMethod;
    type: ReferenceJobType;
    seed?: number;
}

export interface ReferenceJobData extends ReferenceJobDataMutual {
    frame_count: number;
}

export interface ReferenceJobFormData extends ReferenceJobDataMutual {
    quantity: number;
    frame_count: number;
}

interface Props {
    reference: Reference;
}

const defaultQuantity = 5;

function ReferenceJobForm(props: Props): JSX.Element {
    const { reference } = props;
    const { size: referenceSize } = reference;
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const history = useHistory();

    const fetching = useSelector((state: CombinedState) => state.models.fetching);

    const submit = useCallback(async (): Promise<any> => {
        try {
            const values: ReferenceJobFormData = await form.validateFields();
            const data: ReferenceJobData = {
                frame_selection_method: values.frame_selection_method,
                type: values.type,
                seed: values.seed,
                frame_count: values.frame_count,
                reference_id: reference.id,
            };
            const createdJob = await dispatch(createReferenceJobAsync(data));

            return createdJob;
        } catch (e) {
            return false;
        }
    }, [reference]);

    const onSubmit = async (): Promise<void> => {
        const createdJob = await submit();
        if (createdJob) {
            history.push(`/references/${reference.id}/jobs/${createdJob.id}`);
        }
    };

    const onQuantityChange = useCallback(
        (value: number | null) => {
            if (value) {
                const newFrameCount = Math.round((value * referenceSize) / 100);
                form.setFieldsValue({
                    frame_count: newFrameCount,
                });
            }
        },
        [referenceSize],
    );

    const onFrameCountChange = useCallback(
        (value: number | null) => {
            if (value) {
                const newQuantity = Math.floor((value / referenceSize) * 100);
                form.setFieldsValue({
                    quantity: newQuantity,
                });
            }
        },
        [referenceSize],
    );
    const frameCountDescription = 'A representative set, 5-15% of randomly chosen frames is recommended';

    return (
        <Row className='cvat-create-reference-job-form-wrapper'>
            <Col span={24}>
                <Form
                    form={form}
                    layout='vertical'
                    initialValues={{
                        type: ReferenceJobType.GROUND_TRUTH,
                        frame_selection_method: FrameSelectionMethod.RANDOM,
                        quantity: defaultQuantity,
                        frame_count: Math.floor((defaultQuantity * referenceSize) / 100),
                    }}
                >
                    <Col>
                        <Form.Item
                            name='type'
                            label='Job type'
                            rules={[{ required: true, message: 'Please, specify Job type' }]}
                        >
                            <Select virtual={false} className='cvat-select-reference-job-type'>
                                <Select.Option value={ReferenceJobType.GROUND_TRUTH}>Ground truth</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name='frame_selection_method'
                            label='Frame selection method'
                            rules={[{ required: true, message: 'Please, specify frame selection method' }]}
                        >
                            <Select virtual={false} className='cvat-select-frame-selection-method'>
                                <Select.Option value={FrameSelectionMethod.RANDOM}>Random</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Row justify='space-between'>
                            <Col>
                                <Form.Item
                                    name='quantity'
                                    label={(
                                        <Space>
                                            Quantity %
                                            <CVATTooltip title={frameCountDescription}>
                                                <QuestionCircleOutlined style={{ opacity: 0.5 }} />
                                            </CVATTooltip>
                                        </Space>
                                    )}
                                    rules={[{ required: true, message: 'Please, specify quantity' }]}
                                >
                                    <InputNumber
                                        className='cvat-input-frame-quantity'
                                        min={1}
                                        max={100}
                                        size='middle'
                                        onChange={onQuantityChange}
                                    />
                                </Form.Item>
                            </Col>
                            <Col>
                                <Row>
                                    <Col>
                                        <Form.Item
                                            name='frame_count'
                                            label={(
                                                <Space>
                                                    Frame count
                                                    <CVATTooltip title={frameCountDescription}>
                                                        <QuestionCircleOutlined style={{ opacity: 0.5 }} />
                                                    </CVATTooltip>
                                                </Space>
                                            )}
                                            rules={[{ required: true, message: 'Please, specify frame count' }]}
                                        >
                                            <InputNumber
                                                className='cvat-input-frame-count'
                                                min={1}
                                                max={referenceSize}
                                                size='middle'
                                                onChange={onFrameCountChange}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>
                            <Col>
                                <Form.Item name='seed' label='Seed'>
                                    <InputNumber className='cvat-input-seed' size='middle' />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Col>
                </Form>
            </Col>
            <Col span={24} className='cvat-create-reference-job-actions'>
                <Row justify='end'>
                    <Col>
                        <Button
                            className='cvat-submit-reference-job-button'
                            type='primary'
                            onClick={onSubmit}
                            loading={fetching}
                        >
                            Submit
                        </Button>
                    </Col>
                </Row>
            </Col>
        </Row>
    );
}

export default React.memo(ReferenceJobForm);
